import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFamily } from "@/hooks/useFamily";
import { useAuth } from "@/hooks/useAuth";
import {
  useChildCalendarEvents,
  useCalendarEventMutations,
  type CalendarEvent,
} from "@/hooks/useChildCalendar";
import { getDateString, formatDateLong } from "@/lib/family-date";
import { ImportHistorySheet } from "@/components/ImportHistorySheet";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BookOpen,
  Dumbbell,
  Bell,
  CalendarDays,
  Plus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Clock,
  History,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { DateTimeInput } from "@/components/shared/DateTimeInput";
import { RecurrenceInput } from "@/components/shared/RecurrenceInput";

export type ChildCalendarProps = {
  embedded?: boolean;
  weekOffset?: number;
  onWeekOffsetChange?: (offset: number) => void;
  openAdd?: boolean;
  onAddClosed?: () => void;
};

const EVENT_TYPE_CONFIG: Record<string, { icon: typeof BookOpen; label: string; color: string }> = {
  school: { icon: BookOpen, label: "Skole", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  activity: { icon: Dumbbell, label: "Aktivitet", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  deadline: { icon: Clock, label: "Frist", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  notice: { icon: Bell, label: "Melding", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
};

const DAY_LABELS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag"];
const SHORT_DAYS = ["Man", "Tir", "Ons", "Tor", "Fre"];

export default function ChildCalendar({
  embedded = false,
  weekOffset: controlledWeekOffset,
  onWeekOffsetChange,
  openAdd: openAddFromParent,
  onAddClosed,
}: ChildCalendarProps = {}) {
  const { id: childId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { timezone, familyId } = useFamily();
  const [internalWeekOffset, setInternalWeekOffset] = useState(0);
  const weekOffset = embedded && controlledWeekOffset !== undefined ? controlledWeekOffset : internalWeekOffset;
  const setWeekOffset = embedded && onWeekOffsetChange ? onWeekOffsetChange : setInternalWeekOffset;

  const [internalShowAdd, setInternalShowAdd] = useState(false);
  const showAdd = embedded ? ((openAddFromParent ?? false) || internalShowAdd) : internalShowAdd;
  const closeAdd = embedded
    ? () => { onAddClosed?.(); setInternalShowAdd(false); }
    : () => setInternalShowAdd(false);

  const [addDayIndex, setAddDayIndex] = useState(0);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (embedded) return;
    if (searchParams.get("add") === "1") {
      setInternalShowAdd(true);
      setSearchParams({}, { replace: true });
    }
  }, [embedded, searchParams, setSearchParams]);

  useEffect(() => {
    if (embedded && openAddFromParent) {
      setAddDayIndex(0);
    }
  }, [embedded, openAddFromParent]);

  // Add form state
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState<string>("activity");
  const [newStartTime, setNewStartTime] = useState("");
  const [newEndTime, setNewEndTime] = useState("");
  const [newDescription, setNewDescription] = useState("");

  // Date for the event — initialized from clicked day
  const [newEventDate, setNewEventDate] = useState<Date | undefined>(undefined);

  // Recurrence — shared
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState<"daily" | "weekly">("weekly");
  const [weeklyDays, setWeeklyDays] = useState<number[]>([1, 2, 3, 4, 5]);

  // Compute Monday of current week + offset
  const weekDates = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMon + weekOffset * 7);
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, [weekOffset]);

  const weekDateStrings = weekDates.map((d) => getDateString(d, timezone));
  const todayStr = getDateString(new Date(), timezone);
  const isCurrentWeek = weekDateStrings.includes(todayStr);

  const { data: child } = useQuery({
    queryKey: ["child", childId, "calendar"],
    queryFn: async () => {
      if (!childId) return null;
      const { data, error } = await supabase
        .from("children")
        .select("name, avatar_emoji")
        .eq("id", childId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!childId,
  });

  const { data: allEvents = [], isLoading } = useChildCalendarEvents(childId);
  const { addEvent, deleteEvent } = useCalendarEventMutations(childId);

  function getEventsForDay(dayIndex: number): CalendarEvent[] {
    const dateStr = weekDateStrings[dayIndex];
    const dayOfWeek = dayIndex;
    return allEvents.filter((e) => {
      if (e.recurring && e.day_of_week === dayOfWeek) return true;
      if (!e.recurring && e.specific_date === dateStr) return true;
      return false;
    });
  }

  function openAddForDay(dayIndex: number) {
    setAddDayIndex(dayIndex);
    setNewEventDate(weekDates[dayIndex]);
    setInternalShowAdd(true);
  }

  // When sheet opens from parent (embedded), also set date
  useEffect(() => {
    if (showAdd && !newEventDate) {
      setNewEventDate(weekDates[addDayIndex]);
    }
  }, [showAdd]);

  function handleAdd() {
    if (!newTitle.trim() || !childId || !familyId) return;

    if (recurrenceEnabled) {
      // Recurring event: use day_of_week, set valid_from
      const eventDate = newEventDate ?? weekDates[addDayIndex];
      const validFrom = getDateString(eventDate, timezone);

      // For "daily": create one event per selected day type
      // For "weekly": use the selected days
      const days = recurrenceType === "daily" ? [0, 1, 2, 3, 4] : weeklyDays.map(d => d - 1); // convert ISO to 0-indexed Mon

      for (const dayOfWeek of days) {
        addEvent.mutate({
          child_id: childId,
          family_id: familyId,
          event_type: newType as CalendarEvent["event_type"],
          title: newTitle.trim(),
          description: newDescription.trim() || null,
          day_of_week: dayOfWeek,
          specific_date: null,
          start_time: newStartTime || null,
          end_time: newEndTime || null,
          recurring: true,
          source: "manual",
          source_ref: null,
          metadata: {},
          created_by: user?.id ?? null,
          valid_from: validFrom,
        });
      }
      toast.success("Gjentagende hendelse lagt til");
      resetForm();
    } else {
      // One-off event
      const eventDate = newEventDate ?? weekDates[addDayIndex];
      const dateStr = getDateString(eventDate, timezone);

      addEvent.mutate(
        {
          child_id: childId,
          family_id: familyId,
          event_type: newType as CalendarEvent["event_type"],
          title: newTitle.trim(),
          description: newDescription.trim() || null,
          day_of_week: null,
          specific_date: dateStr,
          start_time: newStartTime || null,
          end_time: newEndTime || null,
          recurring: false,
          source: "manual",
          source_ref: null,
          metadata: {},
          created_by: user?.id ?? null,
        },
        {
          onSuccess: () => {
            toast.success("Hendelse lagt til");
            resetForm();
          },
        }
      );
    }
  }

  function resetForm() {
    closeAdd();
    setNewTitle("");
    setNewType("activity");
    setNewStartTime("");
    setNewEndTime("");
    setNewDescription("");
    setNewEventDate(undefined);
    setRecurrenceEnabled(false);
    setRecurrenceType("weekly");
    setWeeklyDays([1, 2, 3, 4, 5]);
  }

  function handleDelete(event: CalendarEvent) {
    deleteEvent.mutate(event.id, {
      onSuccess: () => toast.success("Hendelse slettet"),
    });
  }

  function renderCompactEvent(event: CalendarEvent) {
    const config = EVENT_TYPE_CONFIG[event.event_type] || EVENT_TYPE_CONFIG.notice;
    const Icon = config.icon;
    return (
      <div key={event.id} className="flex items-start gap-2 group">
        <div className={`h-6 w-6 rounded flex items-center justify-center shrink-0 ${config.color}`}>
          <Icon className="h-3 w-3" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium leading-tight truncate">{event.title}</p>
          {event.start_time && (
            <span className="text-[10px] text-muted-foreground">{event.start_time.slice(0, 5)}{event.end_time && `–${event.end_time.slice(0, 5)}`}</span>
          )}
        </div>
        {event.source === "manual" && (
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(event); }}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  function formatWeekLabel(): string {
    const mon = weekDates[0];
    const fri = weekDates[4];
    const monDay = mon.getDate();
    const friDay = fri.getDate();
    const monMonth = mon.toLocaleString("nb-NO", { month: "short" });
    const friMonth = fri.toLocaleString("nb-NO", { month: "short" });
    if (mon.getMonth() === fri.getMonth()) {
      return `${monDay}.–${friDay}. ${monMonth}`;
    }
    return `${monDay}. ${monMonth} – ${friDay}. ${friMonth}`;
  }

  const [weekPopoverOpen, setWeekPopoverOpen] = useState(false);
  const weekLabelDisplay = isCurrentWeek ? "Denne uken" : formatWeekLabel();

  return (
    <div className={cn("min-h-full bg-background pb-6 px-4", embedded ? "pt-2" : "pt-0")}>
      <div className="mx-auto max-w-sm space-y-4">
        {!embedded && (
          <div className="sticky top-0 z-30">
            <div className="border-b border-border bg-background/95 backdrop-blur-sm">
              <div className="px-4 pt-2 pb-1.5">
                <div className="mx-auto max-w-sm flex items-center justify-between">
                  <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-sm font-medium" onClick={() => navigate(`/parent/child/${childId}`)}>
                    Tilbake
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{child?.avatar_emoji || "🧒"}</span>
                    <h1 className="text-lg font-heading font-bold truncate">{child?.name || "Barn"}</h1>
                  </div>
                  <div className="w-9 shrink-0" aria-hidden="true" />
                </div>
              </div>
            </div>
            <div className="bg-background">
              <div className="px-4 pt-1.5 pb-1.5">
                <div className="mx-auto max-w-sm flex items-center justify-end gap-2 min-w-0">
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Popover open={weekPopoverOpen} onOpenChange={setWeekPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "h-7 gap-1 rounded-full px-2.5 text-xs font-medium",
                            weekOffset === 0 ? "text-primary bg-primary/10" : "text-muted-foreground"
                          )}
                        >
                          <CalendarDays className="h-3.5 w-3.5" />
                          {weekLabelDisplay}
                          <ChevronDown className="h-3 w-3 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-popover z-50" align="end">
                        <div className="p-3 pb-2">
                          <span className="text-sm font-medium">Velg uke</span>
                        </div>
                        <div className="px-3 pb-2 flex items-center justify-center gap-2">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setWeekOffset(weekOffset - 1)}>
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm min-w-[120px] text-center">{formatWeekLabel()}</span>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setWeekOffset(weekOffset + 1)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          variant={weekOffset === 0 ? "secondary" : "ghost"}
                          size="sm"
                          className="w-full h-7 text-xs"
                          onClick={() => { setWeekOffset(0); setWeekPopoverOpen(false); }}
                        >
                          Denne uken
                        </Button>
                      </PopoverContent>
                    </Popover>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-full text-xs gap-1 border-primary/40">
                          <Plus className="h-3.5 w-3.5 text-primary" /> Legg til
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => { setAddDayIndex(0); setNewEventDate(weekDates[0]); setInternalShowAdd(true); }}>
                          <CalendarDays className="h-4 w-4 mr-2" />
                          Hendelse
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Week grid */}
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Laster…</div>
        ) : (
          <div className="space-y-3">
            {weekDates.map((date, i) => {
              const dateStr = weekDateStrings[i];
              const isDateToday = dateStr === todayStr;
              const dayEvents = getEventsForDay(i);
              const schoolEvents = dayEvents.filter((e) => e.event_type === "school");
              const otherEvents = dayEvents.filter((e) => e.event_type !== "school");

              return (
                <Card key={dateStr} className={isDateToday ? "border-primary/40 shadow-sm" : ""}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${isDateToday ? "text-primary" : ""}`}>
                          {DAY_LABELS[i]}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {date.getDate()}.
                        </span>
                        {isDateToday && (
                          <Badge variant="default" className="text-[10px] px-1.5 py-0">I dag</Badge>
                        )}
                      </div>
                      <button
                        onClick={() => openAddForDay(i)}
                        className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {dayEvents.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">Ingen hendelser</p>
                    ) : (
                      <div className="space-y-1.5">
                        {schoolEvents.map(renderCompactEvent)}
                        {otherEvents.map(renderCompactEvent)}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-2 text-muted-foreground hover:text-foreground"
          onClick={() => setShowHistory(true)}
        >
          <History className="h-4 w-4" />
          Importhistorikk
        </Button>
      </div>

      {/* Add sheet */}
      <Sheet open={showAdd} onOpenChange={(o) => !o && resetForm()}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Ny hendelse</SheetTitle>
            <SheetDescription>Legg til en hendelse i kalenderen</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="Tittel (f.eks. Fotballtrening)"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="school">📚 Skole</SelectItem>
                <SelectItem value="activity">🏃 Aktivitet</SelectItem>
                <SelectItem value="deadline">⏰ Frist</SelectItem>
                <SelectItem value="notice">🔔 Melding</SelectItem>
              </SelectContent>
            </Select>

            {/* Date & time — shared component */}
            <DateTimeInput
              date={newEventDate}
              onDateChange={setNewEventDate}
              showDate={!recurrenceEnabled}
              time={newStartTime}
              onTimeChange={setNewStartTime}
              endTime={newEndTime}
              onEndTimeChange={setNewEndTime}
              showEndTime
            />

            <Input
              placeholder="Beskrivelse (valgfritt)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />

            {/* Recurrence — shared component */}
            <RecurrenceInput
              enabled={recurrenceEnabled}
              onEnabledChange={setRecurrenceEnabled}
              type={recurrenceType}
              onTypeChange={setRecurrenceType}
              weeklyDays={weeklyDays}
              onWeeklyDaysChange={setWeeklyDays}
            />

            <Button className="w-full" onClick={handleAdd} disabled={!newTitle.trim() || addEvent.isPending}>
              Legg til
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <ImportHistorySheet
        open={showHistory}
        onClose={() => setShowHistory(false)}
        childId={childId || ""}
        childName={child?.name || "Barn"}
      />
    </div>
  );
}
